import { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Edit, Trash2, Eye } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useState, memo } from 'react'
import type { MasterRunbook } from './api'
import { TranslatableText } from '@/components/ui/TranslatableText'
import { buildIconPath } from '@/lib/imagePaths'
import { AutomationBadge } from '@/features/objects/utils/runbookAutomation'
import { DecisionBadge } from '@/features/objects/utils/runbookOptions'
import { Timer, ListChecks } from 'lucide-react'
import { formatMinutesToDHM } from '@/features/objects/utils/formatTime'
import { EntityIcon } from '@/components/ui/entity-icon'
import { sanitizeRichText } from '@/lib/sanitize-html'

interface MasterRunbookActionsProps {
    masterRunbook: MasterRunbook
    /** Absent for a role without master-runbooks.update: the entry is not offered. */
    onEdit?: (masterRunbook: MasterRunbook) => void
    /** Absent for a role without master-runbooks.delete. */
    onDelete?: (masterRunbook: MasterRunbook) => void
    onView: (masterRunbook: MasterRunbook) => void
}

const MasterRunbookActions = memo(function MasterRunbookActions({
    masterRunbook,
    onEdit,
    onDelete,
    onView,
}: MasterRunbookActionsProps) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)

    return (
        <div
            className="flex justify-center"
            onClick={(e) => {
                e.stopPropagation()
            }}
            onMouseDown={(e) => {
                e.stopPropagation()
            }}
            onMouseUp={(e) => {
                e.stopPropagation()
            }}
            onPointerDown={(e) => {
                e.stopPropagation()
            }}
        >
            <DropdownMenu open={open} onOpenChange={setOpen} modal={true}>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">{t('common.openMenu')}</span>
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            onView(masterRunbook)
                            setOpen(false)
                        }}
                    >
                        <Eye className="mr-2 h-4 w-4" />
                        {t('common.view')}
                    </DropdownMenuItem>
                    {onEdit && (
                        <DropdownMenuItem
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                onEdit(masterRunbook)
                                setOpen(false)
                            }}
                        >
                            <Edit className="mr-2 h-4 w-4" />
                            {t('common.edit')}
                        </DropdownMenuItem>
                    )}
                    {onDelete && (
                        <DropdownMenuItem
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                onDelete(masterRunbook)
                                setOpen(false)
                            }}
                            className="text-destructive"
                        >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {t('common.delete')}
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    )
})

export function createMasterRunbookColumns(
    onEdit: ((masterRunbook: MasterRunbook) => void) | undefined,
    onDelete: ((masterRunbook: MasterRunbook) => void) | undefined,
    onView: (masterRunbook: MasterRunbook) => void,
    t: (key: string) => string
): ColumnDef<MasterRunbook>[] {
    return [
        {
            accessorKey: 'description',
            header: t('masterRunbooks.description'),
            cell: ({ row }) => {
                const description = row.getValue('description') as string
                return <div className="font-medium">{description ? <TranslatableText text={description} /> : '-'}</div>
            },
        },
        {
            id: 'icon',
            header: '',
            size: 56,
            cell: ({ row }) => {
                const filename = row.original.iconFilename
                if (!filename) return <span className="text-muted-foreground">-</span>
                return (
                    <EntityIcon src={buildIconPath(filename)} alt="" className="h-7 w-7" />
                )
            },
        },
        {
            accessorKey: 'information',
            header: t('masterRunbooks.information'),
            cell: ({ row }) => {
                const information = row.getValue('information') as string
                if (!information) {
                    return <div className="text-sm text-muted-foreground">-</div>
                }
                return (
                    <div 
                        className="max-w-[400px] text-sm text-muted-foreground line-clamp-2 prose prose-sm"
                        dangerouslySetInnerHTML={{ __html: sanitizeRichText(information) }}
                    />
                )
            },
        },
        {
            id: 'shape',
            header: t('masterRunbooks.shape', 'Tasks & RTO'),
            cell: ({ row }) => {
                const r = row.original
                const tasks = r.taskCount ?? 0
                if (tasks === 0) {
                    return <div className="text-sm text-muted-foreground">{t('masterRunbooks.noTasks', 'No tasks')}</div>
                }
                return (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                            <ListChecks className="h-3.5 w-3.5" aria-hidden />
                            {tasks}
                        </span>
                        {r.duration != null && (
                            <span className="inline-flex items-center gap-1 font-medium text-gray-900 dark:text-gray-100">
                                <Timer className="h-3.5 w-3.5 text-green-500" aria-hidden />
                                {formatMinutesToDHM(r.duration)}
                            </span>
                        )}
                    </div>
                )
            },
        },
        {
            id: 'automation',
            header: t('objects.runbook.automation'),
            cell: ({ row }) => {
                const r = row.original
                if ((r.taskCount ?? 0) === 0) return <div className="text-sm text-muted-foreground">-</div>
                // Counts, not a proportion bar: "3 restore agent" is actionable, a bar is decoration.
                return (
                    <div className="flex flex-wrap items-center gap-1">
                        {(r.agent ?? 0) > 0 && <AutomationBadge mode="agent" count={r.agent} />}
                        {(r.assisted ?? 0) > 0 && <AutomationBadge mode="assisted" count={r.assisted} />}
                        {(r.manual ?? 0) > 0 && <AutomationBadge mode="manual" count={r.manual} />}
                        {(r.decisions ?? 0) > 0 && (
                            <DecisionBadge group={`${r.decisions}`} className="!px-2 !py-0.5 !text-xs" />
                        )}
                    </div>
                )
            },
        },
        {
            id: 'actions',
            header: () => <div className="text-right w-full">{t('common.actions')}</div>,
            cell: ({ row }) => {
                return (
                    <div className="flex justify-end">
                        <MasterRunbookActions
                            masterRunbook={row.original}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onView={onView}
                        />
                    </div>
                )
            },
        },
    ]
}
